import { useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { Button, Modal } from "semantic-ui-react";
import { useTheme } from "styled-components";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { useTrayStatus } from "components/lib/Notifications";

const MIN_BRIEF_LENGTH = 200;

const stripRichText = (value?: string) => {
  if (!value) {
    return "";
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(value, "text/html");
  return doc.body.textContent?.replace(/\s+/g, " ").trim() || "";
};

const getChallengeBriefLength = (
  challenge: Pick<
    OpenAPI.GET<"/challenges/{id}">["response"],
    | "name"
    | "tagline"
    | "description"
    | "instructions"
    | "requirements"
    | "helpfulTips"
    | "reward"
  >,
) =>
  [
    challenge.name,
    challenge.tagline,
    challenge.description,
    challenge.instructions,
    challenge.requirements,
    challenge.helpfulTips,
    challenge.reward,
  ]
    .map((value) => stripRichText(value))
    .filter(Boolean)
    .join(" ").length;

type InnoIntelSearchActionProps = {
  challenge: OpenAPI.Assign<
    OpenAPI.Schemas["Challenge"],
    OpenAPI.Schemas["AssignChallengeOrgOwner"]
  >;
};

const InnoIntelSearchAction = ({ challenge }: InnoIntelSearchActionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const [, setTrayStatus] = useTrayStatus();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStartingSearch, setIsStartingSearch] = useState(false);

  const challengeOrganisation = useMemo(() => {
    if (challenge.ownerOrganisation?._id === challenge.organisation) {
      return challenge.ownerOrganisation;
    }
    if (user?.ownerOrganisation?._id === challenge.organisation) {
      return user.ownerOrganisation;
    }
    return user?.ownerOrganisations?.find(
      (organisation) => organisation._id === challenge.organisation,
    );
  }, [
    challenge.organisation,
    challenge.ownerOrganisation,
    user?.ownerOrganisation,
    user?.ownerOrganisations,
  ]);
  const canManageChallengeBusinesses = useMemo(
    () =>
      util.hasPermission(
        user,
        "org.manageChallenges",
        challenge.organisation,
      ) || util.hasPermission(user, "challenge.viewSettings", challenge._id),
    [challenge._id, challenge.organisation, user],
  );
  const hasInnointelAccess = useMemo(
    () =>
      !!challengeOrganisation?.enabledFeatures?.includes(
        "innovationIntelligence",
      ) &&
      util.hasPermission(
        user,
        "org.innovationIntelligence",
        challenge.organisation,
      ),
    [challenge.organisation, challengeOrganisation, user],
  );
  const hasSufficientBrief = useMemo(
    () => getChallengeBriefLength(challenge) >= MIN_BRIEF_LENGTH,
    [challenge],
  );

  const canQueryLinkedSearch =
    !!challenge._id && hasInnointelAccess && canManageChallengeBusinesses;
  const linkedSearchQuery = useQuery(
    initUseQuery(
      api.innointel.getSearchByContext,
      [{ challenge: challenge._id }],
      { isEnabled: canQueryLinkedSearch },
    ),
  );
  const linkedSearch = linkedSearchQuery.data?.search;

  const canCreateSearch =
    hasInnointelAccess && canManageChallengeBusinesses && hasSufficientBrief;
  const canShowAction =
    hasInnointelAccess && canManageChallengeBusinesses && canCreateSearch;

  if (!user?._id || !canShowAction) {
    return null;
  }

  return (
    <>
      <Button
        size={theme.sizes.isMobile ? "mini" : undefined}
        loading={linkedSearchQuery.isLoading}
        icon={linkedSearch ? "external" : "building outline"}
        content={
          linkedSearch ?
            t("challenge.businesses.viewTitle")
          : t("challenge.businesses.findTitle")
        }
        secondary={!linkedSearch}
        onClick={() => {
          if (linkedSearch) {
            navigate(`/innointel/searches/${linkedSearch._id}`);
            return;
          }
          setIsModalOpen(true);
        }}
      />
      <Modal
        open={isModalOpen}
        onClose={() => {
          if (!isStartingSearch) {
            setIsModalOpen(false);
          }
        }}
        size="small"
        mountNode={document.getElementById("semantic-modal-mount-node")}
      >
        <Modal.Header>{t("challenge.businesses.findTitle")}</Modal.Header>
        <Modal.Content>
          <p>
            We will automatically analyse this {t("generic.challenge")} brief
            and derive suitable parameters for a business search based on its
            contents.
          </p>
          <p>
            You can stay on this {t("generic.challenge")} page while the search
            runs. We will notify you when the first results are ready to review.
          </p>
          <p>
            If no location can be determined from the {t("generic.challenge")},
            the search will default to the United Kingdom.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            content="Cancel"
            onClick={() => setIsModalOpen(false)}
            disabled={isStartingSearch}
          />
          <Button
            primary
            content="Start search"
            loading={isStartingSearch}
            onClick={() => {
              setIsStartingSearch(true);
              api.users.createLongRunningTask(
                user._id,
                {
                  forType: "challenge",
                  forId: challenge._id,
                  type: "challengeInnointelSearch",
                },
                () => {
                  setIsStartingSearch(false);
                  setIsModalOpen(false);
                  setTrayStatus({ open: true, activeIndex: 0 });
                  api.invalidateQueries(api.notifications.get, []);
                  api.invalidateQueries(api.innointel.getSearches, []);
                  api.invalidateQueries(api.innointel.getSearchByContext, [
                    { challenge: challenge._id },
                  ]);
                },
                (error) => {
                  setIsStartingSearch(false);
                  toast.error(
                    error?.message ||
                      "We could not start the business search right now.",
                  );
                },
              );
            }}
          />
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default InnoIntelSearchAction;
